'use server'

import { apiRequest, ApiResponse } from '../request'

export type StorageUnitType =
    | 'REFRIGERATOR'
    | 'FREEZER'
    | 'ULTRA_FREEZER'
    | 'AMBIENT'

export type TemperatureAlertType =
    | 'TEMP_HIGH'
    | 'TEMP_LOW'
    | 'TEMP_CRITICAL'
    | 'SENSOR_OFFLINE'

export type TemperatureAlertStatus =
    | 'TEMP_ALERT_ACTIVE'
    | 'TEMP_ALERT_ACKNOWLEDGED'
    | 'TEMP_ALERT_RESOLVED'

export interface StorageUnit {
    id: number
    companyId: number
    name: string
    type: StorageUnitType
    location?: string
    minTemp: number
    maxTemp: number
    criticalMinTemp: number
    criticalMaxTemp: number
    sensorId?: string
    isActive: boolean
    createdAt: string
    updatedAt: string
    temperatureLogs?: TemperatureLog[]
    temperatureAlerts?: TemperatureAlert[]
}

export interface TemperatureLog {
    id: number
    storageUnitId: number
    temperature: number
    humidity?: number
    timestamp: string
    isManual: boolean
    userId?: number
    user?: {
        id: number
        name: string
    }
}

export interface TemperatureAlert {
    id: number
    storageUnitId: number
    type: TemperatureAlertType
    status: TemperatureAlertStatus
    triggeredAt: string
    temperature?: number
    acknowledgedAt?: string
    acknowledgedById?: number
    resolvedAt?: string
    resolvedById?: number
    notes?: string
    storageUnit?: StorageUnit
}

export interface CreateStorageUnitData {
    name: string
    type: StorageUnitType
    location?: string
    minTemp: number
    maxTemp: number
    criticalMinTemp?: number
    criticalMaxTemp?: number
    sensorId?: string
}

export interface UpdateStorageUnitData extends Partial<CreateStorageUnitData> {
    isActive?: boolean
}

export interface LogTemperatureData {
    temperature: number
    humidity?: number
    timestamp?: string
}

export interface StorageUnitFilters {
    type?: StorageUnitType
    isActive?: boolean
}

export interface TemperatureReport {
    storageUnitId: number
    storageUnit: StorageUnit
    period: {
        start: string
        end: string
    }
    stats: {
        min: number
        max: number
        avg: number
        readings: number
    }
    excursions: {
        count: number
        totalDuration: number
    }
    compliance: number
}

// Storage Unit CRUD
export async function getStorageUnits(
    filters?: StorageUnitFilters
): Promise<ApiResponse<StorageUnit[]>> {
    return apiRequest<StorageUnit[]>('stock/temperature/units', {
        params: filters,
    })
}

export async function getStorageUnit(
    id: number
): Promise<ApiResponse<StorageUnit>> {
    return apiRequest<StorageUnit>(`stock/temperature/units/${id}`, {})
}

export async function createStorageUnit(
    data: CreateStorageUnitData
): Promise<ApiResponse<StorageUnit>> {
    return apiRequest<StorageUnit>('stock/temperature/units', { data }, 'post')
}

export async function updateStorageUnit(
    id: number,
    data: UpdateStorageUnitData
): Promise<ApiResponse<StorageUnit>> {
    return apiRequest<StorageUnit>(
        `stock/temperature/units/${id}`,
        { data },
        'patch'
    )
}

export async function deleteStorageUnit(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`stock/temperature/units/${id}`, {}, 'delete')
}

// Temperature Logs
export async function logTemperature(
    unitId: number,
    data: LogTemperatureData
): Promise<ApiResponse<TemperatureLog>> {
    return apiRequest<TemperatureLog>(
        `stock/temperature/units/${unitId}/log`,
        { data },
        'post'
    )
}

export async function getTemperatureLogs(
    unitId: number,
    filters?: { startDate?: string; endDate?: string }
): Promise<ApiResponse<TemperatureLog[]>> {
    return apiRequest<TemperatureLog[]>(
        `stock/temperature/units/${unitId}/logs`,
        { params: filters }
    )
}

// Alerts
export async function getTemperatureAlerts(filters?: {
    storageUnitId?: number
    status?: TemperatureAlertStatus
}): Promise<ApiResponse<TemperatureAlert[]>> {
    return apiRequest<TemperatureAlert[]>('stock/temperature/alerts', {
        params: filters,
    })
}

export async function acknowledgeAlert(
    id: number,
    notes?: string
): Promise<ApiResponse<TemperatureAlert>> {
    return apiRequest<TemperatureAlert>(
        `stock/temperature/alerts/${id}/acknowledge`,
        { data: { notes } },
        'patch'
    )
}

export async function resolveAlert(
    id: number,
    notes?: string
): Promise<ApiResponse<TemperatureAlert>> {
    return apiRequest<TemperatureAlert>(
        `stock/temperature/alerts/${id}/resolve`,
        { data: { notes } },
        'patch'
    )
}

// Reports
export async function getTemperatureReport(
    unitId: number,
    startDate: string,
    endDate: string
): Promise<ApiResponse<TemperatureReport>> {
    return apiRequest<TemperatureReport>(
        `stock/temperature/units/${unitId}/report`,
        { params: { startDate, endDate } }
    )
}

// Labels
export const STORAGE_UNIT_TYPE_LABELS: Record<StorageUnitType, string> = {
    REFRIGERATOR: 'Geladeira',
    FREEZER: 'Freezer',
    ULTRA_FREEZER: 'Ultra Freezer',
    AMBIENT: 'Ambiente',
}

export const TEMPERATURE_ALERT_TYPE_LABELS: Record<
    TemperatureAlertType,
    string
> = {
    TEMP_HIGH: 'Temperatura Alta',
    TEMP_LOW: 'Temperatura Baixa',
    TEMP_CRITICAL: 'Temperatura Critica',
    SENSOR_OFFLINE: 'Sensor Offline',
}

export const TEMPERATURE_ALERT_STATUS_LABELS: Record<
    TemperatureAlertStatus,
    string
> = {
    TEMP_ALERT_ACTIVE: 'Ativo',
    TEMP_ALERT_ACKNOWLEDGED: 'Reconhecido',
    TEMP_ALERT_RESOLVED: 'Resolvido',
}

export const TEMPERATURE_ALERT_STATUS_COLORS: Record<
    TemperatureAlertStatus,
    string
> = {
    TEMP_ALERT_ACTIVE: 'badge-error',
    TEMP_ALERT_ACKNOWLEDGED: 'badge-warning',
    TEMP_ALERT_RESOLVED: 'badge-success',
}
